import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  CircularProgress,
  Alert,
  Container,
  Breadcrumbs,
  Chip,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getMySavedProducts, removeSavedProduct } from "../../../../services/UserApi/UserApi";
import { getImageUrl } from "../../../../services/indexMediaBaseUrl";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function SavedProducts() {
  const { t, i18n } = useTranslation("saved-products");
  const isAr = (i18n?.language || "ar").startsWith("ar");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState([]);
  const [deletingIds, setDeletingIds] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchSaved = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await getMySavedProducts();
        const list = res?.saved_products || res?.data?.saved_products || [];

        if (mounted) setSaved(Array.isArray(list) ? list : []);
      } catch (err) {
        if (mounted) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              t("failed_fetch_saved", "فشل جلب العناصر المحفوظة")
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchSaved();

    return () => {
      mounted = false;
    };
  }, [i18n.language, t]);

  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfoForProduct = (prod) => {
    if (!prod) return null;
    const countryId = getSelectedCountryId();
    const curr = prod.currencies || prod.Currencies || [];
    let entry = null;
    if (countryId) {
      entry = curr.find((c) => String(c.country_id) === String(countryId));
    }
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      if (prod.price || prod.price_after_discount) {
        return {
          price: prod.price ? String(prod.price) : null,
          price_after_discount: prod.price_after_discount ? String(prod.price_after_discount) : null,
        };
      }
      return null;
    }
    return {
      price: entry.price ? String(entry.price) : null,
      price_after_discount: entry.price_after_discount ? String(entry.price_after_discount) : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "primary.main" }} size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert 
          severity="error"
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(211, 47, 47, 0.15)",
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!saved || saved.length === 0) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <FavoriteIcon
              sx={{
                fontSize: 80,
                color: "grey.300",
                mb: 2,
              }}
            />
            <Typography variant="h5" color="text.secondary" fontWeight={700} gutterBottom>
              {t("no_saved_products", "لا توجد منتجات محفوظة")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {isAr
                ? "ابدأ بحفظ منتجاتك المفضلة لتصفحها لاحقاً"
                : "Start saving your favorite products to browse later"}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />}
          sx={{ mb: 4 }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <HomeIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {isAr ? "الرئيسية" : "Home"}
            </Typography>
          </Link>
          <Typography variant="body2" color="text.primary" fontWeight={700}>
            {t("saved_products", "المنتجات المحفوظة")}
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 6,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              px: 3,
              py: 1.5,
              borderRadius: 50,
              background: "linear-gradient(135deg, rgba(213, 170, 47, 0.1) 0%, rgba(230, 199, 106, 0.1) 100%)",
              border: "1px solid rgba(213, 170, 47, 0.2)",
            }}
          >
            <FavoriteIcon sx={{ color: "primary.main", fontSize: 32 }} />
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("saved_products", "المنتجات المحفوظة")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, lineHeight: 1.8 }}
          >
            {isAr
              ? `${saved.length} منتج محفوظ في قائمتك`
              : `${saved.length} products saved in your list`}
          </Typography>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={3}>
          {saved.map((entry, index) => {
            const p = entry?.product || {};
            const name = isAr ? p.name_ar || p.name_en : p.name_en || p.name_ar;
            const desc = isAr ? p.desc_ar : p.desc_en;
            const img = getImageUrl(p.main_photo || "");
            const productId = entry?.product_id ?? p?.id;
            const isDeleting = deletingIds.includes(productId);

            const handleRemove = async () => {
              if (!productId) return;
              setDeletingIds((prev) => [...prev, productId]);

              try {
                await removeSavedProduct(productId);
                setSaved((prev) =>
                  prev.filter(
                    (it) => (it?.product_id ?? it?.product?.id) !== productId
                  )
                );
              } catch (err) {
                console.error("removeSavedProduct error:", err);
              } finally {
                setDeletingIds((prev) => prev.filter((id) => id !== productId));
              }
            };

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={entry?.id || productId}>
                <Card
                  sx={{
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 32px rgba(213, 170, 47, 0.15)",
                      "& .product-image": {
                        transform: "scale(1.1)",
                      },
                      "& .delete-btn": {
                        opacity: 1,
                      },
                    },
                    animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
                    "@keyframes fadeInUp": {
                      from: {
                        opacity: 0,
                        transform: "translateY(20px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    },
                  }}
                >
                  {/* Delete Button */}
                  <IconButton
                    size="small"
                    onClick={handleRemove}
                    className="delete-btn"
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      zIndex: 2,
                      bgcolor: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(8px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      opacity: 0,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "#fff",
                        transform: "scale(1.1)",
                      },
                    }}
                    disabled={isDeleting}
                    aria-label={t("remove_saved", "إزالة من المحفوظة")}
                  >
                    {isDeleting ? (
                      <CircularProgress size={18} sx={{ color: "primary.main" }} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>

                  <CardActionArea
                    component={Link}
                    to={`/product/${p.id}`}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                    }}
                  >
                    {/* Product Image */}
                    <Box sx={{ position: "relative", overflow: "hidden", height: 240, bgcolor: "grey.50" }}>
                      <CardMedia
                        component="img"
                        image={img || "/placeholder.png"}
                        alt={name || t("product_image_alt", "صورة المنتج")}
                        className="product-image"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.4) 100%)",
                        }}
                      />
                    </Box>

                    {/* Product Info */}
                    <Box sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          mb: 1.5,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.4,
                          minHeight: "2.8em",
                        }}
                      >
                        {name}
                      </Typography>

                      {desc && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.6,
                          }}
                        >
                          {desc}
                        </Typography>
                      )}

                      {/* Price Display */}
                      {(() => {
                        const pinfo = getPriceInfoForProduct(p);
                        if (!pinfo) return null;
                        const orig = formatPrice(pinfo.price);
                        const disc = formatPrice(pinfo.price_after_discount);
                        return (
                          <Box
                            sx={{
                              mt: "auto",
                              pt: 2,
                              borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                            }}
                          >
                            {disc ? (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration: "line-through",
                                    color: "text.disabled",
                                    fontWeight: 600,
                                    mb: 0.5,
                                  }}
                                >
                                  {orig} {isAr ? "ج.م" : "EGP"}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "primary.main",
                                      fontWeight: 800,
                                    }}
                                  >
                                    {disc} {isAr ? "ج.م" : "EGP"}
                                  </Typography>
                                  <Chip
                                    label={`-${Math.round((1 - parseFloat(disc) / parseFloat(orig)) * 100)}%`}
                                    size="small"
                                    sx={{
                                      bgcolor: "error.main",
                                      color: "#fff",
                                      fontWeight: 700,
                                      fontSize: "0.7rem",
                                      height: 22,
                                    }}
                                  />
                                </Box>
                              </Box>
                            ) : (
                              <Typography
                                variant="h5"
                                sx={{
                                  color: "primary.main",
                                  fontWeight: 800,
                                }}
                              >
                                {orig} {isAr ? "ج.م" : "EGP"}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
